/**
 * GWH Wishlist — client-side (localStorage) favourites.
 * - Toggles heart buttons on product cards
 * - Keeps a header count badge in sync
 * - Renders saved items in the wishlist drawer (no cart/AJAX dependency)
 */
(function () {
  'use strict';

  var KEY = 'gwh-wishlist';

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {}
  }

  function has(handle) {
    return read().some(function (p) {
      return p.handle === handle;
    });
  }

  function toggle(item) {
    var list = read();
    var exists = list.some(function (p) {
      return p.handle === item.handle;
    });
    if (exists) {
      list = list.filter(function (p) {
        return p.handle !== item.handle;
      });
    } else {
      list.unshift(item);
    }
    write(list);
    return !exists; // true if now saved
  }

  function itemFromButton(btn) {
    return {
      handle: btn.getAttribute('data-handle'),
      title: btn.getAttribute('data-title'),
      url: btn.getAttribute('data-url'),
      vendor: btn.getAttribute('data-vendor'),
      image: btn.getAttribute('data-image'),
      price: btn.getAttribute('data-price')
    };
  }

  function syncButtons() {
    document.querySelectorAll('[data-gwh-wishlist-toggle]').forEach(function (btn) {
      var saved = has(btn.getAttribute('data-handle'));
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  }

  function syncCount() {
    var count = read().length;
    document.querySelectorAll('[data-gwh-wishlist-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  // Only allow http(s), root-relative, or protocol-relative URLs (blocks
  // javascript:/data: and other script-bearing schemes from stored values).
  function safeUrl(url) {
    if (typeof url !== 'string') return '#';
    var u = url.trim();
    if (/^(https?:\/\/|\/|\.\/)/i.test(u)) return u;
    return '#';
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text; // textContent — never HTML
    return node;
  }

  function renderDrawer() {
    var body = document.querySelector('[data-gwh-wishlist-body]');
    var empty = document.querySelector('[data-gwh-wishlist-empty]');
    if (!body) return;
    var list = read();
    body.textContent = '';
    if (empty) empty.hidden = list.length !== 0;
    if (!list.length) return;

    var frag = document.createDocumentFragment();
    list.forEach(function (p) {
      var row = el('div', 'gwh-wishlist-item');

      var media = el('a', 'gwh-wishlist-item__media');
      media.href = safeUrl(p.url);
      if (p.image) {
        var img = document.createElement('img');
        img.src = safeUrl(p.image);
        img.alt = '';
        img.loading = 'lazy';
        img.width = 120;
        img.height = 160;
        media.appendChild(img);
      }

      var info = el('div', 'gwh-wishlist-item__info');
      if (p.vendor) info.appendChild(el('p', 'gwh-wishlist-item__brand', p.vendor));
      var title = el('a', 'gwh-wishlist-item__title', p.title || '');
      title.href = safeUrl(p.url);
      info.appendChild(title);
      if (p.price) info.appendChild(el('p', 'gwh-wishlist-item__price', p.price));

      var remove = el('button', 'gwh-wishlist-item__remove', '\u00d7');
      remove.type = 'button';
      remove.setAttribute('data-gwh-wishlist-remove', p.handle || '');
      remove.setAttribute('aria-label', 'Remove from wishlist');

      row.appendChild(media);
      row.appendChild(info);
      row.appendChild(remove);
      frag.appendChild(row);
    });
    body.appendChild(frag);
  }

  var drawer;

  function openDrawer() {
    drawer = drawer || document.querySelector('[data-gwh-wishlist-drawer]');
    if (!drawer) return;
    renderDrawer();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var panel = drawer.querySelector('.gwh-wishlist-drawer__panel');
    if (panel) panel.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function init() {
    syncButtons();
    syncCount();

    // Delegated clicks (survives re-rendered cards / drawer content).
    document.addEventListener('click', function (event) {
      var toggleBtn = event.target.closest('[data-gwh-wishlist-toggle]');
      if (toggleBtn) {
        event.preventDefault();
        event.stopPropagation();
        toggle(itemFromButton(toggleBtn));
        syncButtons();
        syncCount();
        if (drawer && drawer.classList.contains('is-open')) renderDrawer();
        return;
      }

      if (event.target.closest('[data-gwh-wishlist-open]')) {
        event.preventDefault();
        openDrawer();
        return;
      }

      if (event.target.closest('[data-gwh-wishlist-close]')) {
        closeDrawer();
        return;
      }

      var removeBtn = event.target.closest('[data-gwh-wishlist-remove]');
      if (removeBtn) {
        var handle = removeBtn.getAttribute('data-gwh-wishlist-remove');
        write(
          read().filter(function (p) {
            return p.handle !== handle;
          })
        );
        syncButtons();
        syncCount();
        renderDrawer();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Re-sync heart state when product cards are added/replaced (collection
    // filtering + infinite scroll swap grid contents via the Section Rendering
    // API). Without this, restored cards show as unsaved and a click would
    // silently remove the item from storage. Debounced to stay cheap.
    var syncTimer = null;
    var observer = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (m) {
        return [].some.call(m.addedNodes, function (n) {
          return (
            n.nodeType === 1 &&
            (n.matches && n.matches('[data-gwh-wishlist-toggle]')
              ? true
              : n.querySelector && n.querySelector('[data-gwh-wishlist-toggle]'))
          );
        });
      });
      if (!relevant) return;
      clearTimeout(syncTimer);
      syncTimer = setTimeout(syncButtons, 120);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
